import { Pause } from '@/components/icons/pauseIcon';
import { PlayIcon } from '@/components/icons/playIcon';
import { FlagIcon } from '@/components/icons/FlagIcon';
import { DeleteButtonIcon } from '@/components/icons/deleteButtonIcon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { pad2 } from '@/shared/lib/pad2';

function onlyDigits(val: string) {
  return val.replace(/\D/g, '');
}

function clampInt(val: string, min: number, max: number) {
  const n = Number(onlyDigits(val));
  if (!Number.isFinite(n)) {
    return min;
  }
  return Math.min(max, Math.max(min, n));
}

export function TaskBottomBar({
  isCompleted,
  isRunning,
  plannedSeconds,
  plannedStr,
  spentStr,
  isOverdue,
  canDeleteThisTask,
  onStartPause,
  remainingLabel,
  onFinish,
  onJump,
  onDelete,
  onSavePlan,
  jumpPending,
  jumpDisabled,
}: {
  isCompleted: boolean;
  isRunning: boolean;
  plannedSeconds: number;
  plannedStr: string;
  spentStr: string;
  remainingStr: string;
  remainingLabel: string;
  isOverdue: boolean;
  canDeleteThisTask: boolean;

  onStartPause: () => void;
  onFinish: () => void;
  onJump: () => void;
  onDelete: () => void;

  onSavePlan: (nextSeconds: number) => Promise<void> | void;

  jumpPending: boolean;
  jumpDisabled: boolean;
}) {
  const [isEditingPlan, setIsEditingPlan] = useState(false);

  const plannedHoursInitial = useMemo(
    () => Math.floor((plannedSeconds ?? 0) / 3600),
    [plannedSeconds]
  );
  const plannedMinutesInitial = useMemo(
    () => Math.floor(((plannedSeconds ?? 0) % 3600) / 60),
    [plannedSeconds]
  );

  const [planHRaw, setPlanHRaw] = useState(pad2(plannedHoursInitial));
  const [planMRaw, setPlanMRaw] = useState(pad2(plannedMinutesInitial));

  const planHRef = useRef<HTMLInputElement | null>(null);
  const planMRef = useRef<HTMLInputElement | null>(null);
  const planWrapRef = useRef<HTMLDivElement | null>(null);

  // синхронизация, когда план меняется снаружи
  useEffect(() => {
    if (isEditingPlan) {
      return;
    }
    setPlanHRaw(pad2(plannedHoursInitial));
    setPlanMRaw(pad2(plannedMinutesInitial));
  }, [isEditingPlan, plannedHoursInitial, plannedMinutesInitial]);

  function getPlanFromInputs() {
    const hVal = planHRef.current?.value ?? planHRaw;
    const mVal = planMRef.current?.value ?? planMRaw;

    const h = clampInt(hVal, 0, 99);
    const m = clampInt(mVal, 0, 59);

    return { h, m, nextSeconds: h * 3600 + m * 60 };
  }

  function closePlanEdit(reset = true) {
    setIsEditingPlan(false);
    if (reset) {
      setPlanHRaw(pad2(plannedHoursInitial));
      setPlanMRaw(pad2(plannedMinutesInitial));
    }
  }

  async function savePlanEdit() {
    const { h, m, nextSeconds } = getPlanFromInputs();

    setPlanHRaw(pad2(h));
    setPlanMRaw(pad2(m));

    const currentSeconds = Number(plannedSeconds ?? 0);
    if (nextSeconds === currentSeconds) {
      setIsEditingPlan(false);
      return;
    }

    try {
      await onSavePlan(nextSeconds);
      setIsEditingPlan(false);
    } catch {}
  }

  function openPlanEdit() {
    if (isCompleted) {
      return;
    }
    const h = Math.floor((plannedSeconds ?? 0) / 3600);
    const m = Math.floor(((plannedSeconds ?? 0) % 3600) / 60);

    setPlanHRaw(pad2(h));
    setPlanMRaw(pad2(m));
    setIsEditingPlan(true);

    setTimeout(() => {
      planHRef.current?.focus();
      planHRef.current?.select();
    }, 0);
  }

  function onPlanKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      savePlanEdit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      closePlanEdit(true);
    }
  }

  useEffect(() => {
    if (!isEditingPlan) {
      return;
    }

    function onPointerDown(e: MouseEvent | TouchEvent) {
      const wrap = planWrapRef.current;
      const target = e.target as Node | null;

      if (wrap && target && wrap.contains(target)) {
        return;
      }

      savePlanEdit();
    }

    document.addEventListener('touchstart', onPointerDown, true);
    document.addEventListener('mousedown', onPointerDown, true);

    return () => {
      document.removeEventListener('touchstart', onPointerDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
    };
  }, [isEditingPlan, planHRaw, planMRaw, plannedSeconds]);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[80]">
      <div className="mx-auto max-w-md pb-[env(safe-area-inset-bottom)] bg-[#0B1A2A]">
        <div className="px-[15px] grid grid-cols-[1fr_1fr_1fr_39px] gap-[4px]">
          <button
            type="button"
            disabled={isCompleted}
            onClick={onStartPause}
            className="h-[40px] rounded-[14px] ui-glow-desktop-lite border border-white bg-white/5 active:scale-[0.98] transition flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label={isRunning ? 'Пауза' : 'Старт'}
            title={isRunning ? 'Пауза' : 'Старт'}
          >
            {isRunning ? (
              <Pause width={21} height={21} />
            ) : (
              <PlayIcon width={21} height={21} />
            )}
          </button>

          <button
            type="button"
            disabled={isCompleted}
            onClick={onFinish}
            className="h-[40px] rounded-[14px] ui-glow-desktop-lite border border-white bg-white/5 active:scale-[0.98] transition flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="Завершить"
            title="Завершить"
          >
            <FlagIcon width={21} height={21} />
          </button>

          <button
            type="button"
            disabled={jumpDisabled}
            onClick={onJump}
            className="h-[40px] rounded-[14px] ui-glow-desktop-lite border border-white bg-white/5 active:scale-[0.98] transition flex items-center justify-center text-white text-[13px] font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="Jump"
            title="Jump"
          >
            {jumpPending ? '…' : 'Jump'}
          </button>

          <button
            type="button"
            onClick={onDelete}
            disabled={!canDeleteThisTask}
            className="
                    h-[40px] rounded-[30px] border border-[#EF4642] w-[39px]
                   flex items-center justify-center
                    transition active:scale-[0.98]
                    disabled:opacity-40
                    disabled:cursor-not-allowed
                    disabled:active:scale-100
                "
            aria-label="Удалить задачу"
            title={
              canDeleteThisTask
                ? 'Удалить задачу'
                : 'Недостаточно прав для удаления'
            }
          >
            <DeleteButtonIcon />
          </button>
        </div>

        <div className="px-[15px] mt-[12px] pb-1 grid grid-cols-3 text-[16px] leading-none">
          <div className="flex flex-col items-center justify-start">
            {!isEditingPlan ? (
              <button
                type="button"
                onClick={openPlanEdit}
                disabled={isCompleted}
                className="text-white text-center text-[14px] mb-1 disabled:opacity-60 disabled:cursor-not-allowed leading-none tracking-[-0.83px]"
                title={
                  isCompleted ? 'Задача завершена' : 'Нажми, чтобы изменить'
                }
              >
                Заявлено {plannedStr}
              </button>
            ) : (
              <div
                ref={planWrapRef}
                className="inline-flex items-center gap-1 text-white text-[14px] text-center tracking-[-0.83px]"
              >
                <span className="text-white/80">Заявлено</span>

                <div className="flex items-center">
                  <input
                    ref={planHRef}
                    value={planHRaw}
                    onChange={(e) =>
                      setPlanHRaw(onlyDigits(e.target.value).slice(0, 2))
                    }
                    onBlur={() => setPlanHRaw(pad2(clampInt(planHRaw, 0, 99)))}
                    onKeyDown={onPlanKeyDown}
                    inputMode="numeric"
                    className="w-[26px] h-[14px] rounded-[6px] bg-white/15 border border-white/20 text-white text-center text-[14px] outline-none focus:border-white/40 tracking-[-0.83px]"
                    style={{
                      colorScheme: 'dark',
                      WebkitTextFillColor: 'white',
                    }}
                  />
                  <span className="text-white/80">ч</span>

                  <input
                    ref={planMRef}
                    value={planMRaw}
                    onChange={(e) =>
                      setPlanMRaw(onlyDigits(e.target.value).slice(0, 2))
                    }
                    onBlur={() => setPlanMRaw(pad2(clampInt(planMRaw, 0, 59)))}
                    onKeyDown={onPlanKeyDown}
                    inputMode="numeric"
                    className="w-[26px] h-[14px] rounded-[6px] bg-white/15 border border-white/20 text-white text-center text-[14px] tracking-[-0.83px] outline-none focus:border-white/40"
                    style={{
                      colorScheme: 'dark',
                      WebkitTextFillColor: 'white',
                    }}
                  />
                  <span className=" text-white/80">м</span>
                </div>
              </div>
            )}
          </div>

          <div className="text-white text-center">
            <div className="text-white text-[14px] tracking-[-0.83px] mb-1">
              Потрачено {spentStr}
            </div>
          </div>

          <div className="text-center tracking-[-0.83px]">
            <div
              className={`text-[14px]
                ${
                  isOverdue
                    ? 'font-medium text-red-400'
                    : 'font-medium text-[#57B792]'
                }
              `}
            >
              {remainingLabel}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
